import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// ─── Types ────────────────────────────────────────────────────────────

export interface Symptom {
  id: number;
  name: string;
  description: string;
  severity: number;
  body_area: string;
  is_active: boolean;
  first_noticed: string | null;
  category: string;
  ai_categorized: boolean;
  created_at: string;
  updated_at: string;
}

export interface SymptomLog {
  id: number;
  symptom: number;
  severity: number;
  notes: string;
  logged_at: string;
  created_at: string;
  updated_at: string;
}

export interface MedicalCondition {
  id: number;
  name: string;
  diagnosed_date: string | null;
  status: 'active' | 'managed' | 'resolved';
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface Allergy {
  id: number;
  name: string;
  allergy_type: 'food' | 'drug' | 'environmental' | 'other';
  severity: 'mild' | 'moderate' | 'severe';
  reaction: string;
  created_at: string;
  updated_at: string;
}

export interface PastSurgery {
  id: number;
  name: string;
  surgery_date: string | null;
  hospital: string;
  surgeon: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface DietaryRestriction {
  id: number;
  name: string;
  restriction_type: 'allergy' | 'intolerance' | 'preference' | 'medical';
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface FoodLog {
  id: number;
  food_item: string;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  notes: string;
  logged_at: string;
  created_at: string;
  updated_at: string;
}

// ─── Service ──────────────────────────────────────────────────────────

const symptomsService = {
  // Symptoms
  async getSymptoms(): Promise<Symptom[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.symptoms);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createSymptom(data: Partial<Symptom>): Promise<Symptom> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.symptoms, data);
    return res.data;
  },
  async updateSymptom(id: number, data: Partial<Symptom>): Promise<Symptom> {
    const res = await axiosInstance.patch(API_ENDPOINTS.symptoms.symptomDetail(id), data);
    return res.data;
  },
  async deleteSymptom(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.symptomDetail(id));
  },

  // Symptom Logs
  async getSymptomLogs(symptomId?: number): Promise<SymptomLog[]> {
    const params = symptomId ? { symptom_id: symptomId } : {};
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.symptomLogs, { params });
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createSymptomLog(data: Partial<SymptomLog>): Promise<SymptomLog> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.symptomLogs, data);
    return res.data;
  },
  async deleteSymptomLog(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.symptomLogDetail(id));
  },

  // Medical Conditions
  async getMedicalConditions(): Promise<MedicalCondition[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.medicalConditions);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createMedicalCondition(data: Partial<MedicalCondition>): Promise<MedicalCondition> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.medicalConditions, data);
    return res.data;
  },
  async updateMedicalCondition(id: number, data: Partial<MedicalCondition>): Promise<MedicalCondition> {
    const res = await axiosInstance.patch(API_ENDPOINTS.symptoms.medicalConditionDetail(id), data);
    return res.data;
  },
  async deleteMedicalCondition(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.medicalConditionDetail(id));
  },

  // Allergies
  async getAllergies(): Promise<Allergy[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.allergies);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createAllergy(data: Partial<Allergy>): Promise<Allergy> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.allergies, data);
    return res.data;
  },
  async updateAllergy(id: number, data: Partial<Allergy>): Promise<Allergy> {
    const res = await axiosInstance.patch(API_ENDPOINTS.symptoms.allergyDetail(id), data);
    return res.data;
  },
  async deleteAllergy(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.allergyDetail(id));
  },

  // Past Surgeries
  async getPastSurgeries(): Promise<PastSurgery[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.pastSurgeries);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createPastSurgery(data: Partial<PastSurgery>): Promise<PastSurgery> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.pastSurgeries, data);
    return res.data;
  },
  async updatePastSurgery(id: number, data: Partial<PastSurgery>): Promise<PastSurgery> {
    const res = await axiosInstance.patch(API_ENDPOINTS.symptoms.pastSurgeryDetail(id), data);
    return res.data;
  },
  async deletePastSurgery(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.pastSurgeryDetail(id));
  },

  // Dietary Restrictions
  async getDietaryRestrictions(): Promise<DietaryRestriction[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.dietaryRestrictions);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createDietaryRestriction(data: Partial<DietaryRestriction>): Promise<DietaryRestriction> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.dietaryRestrictions, data);
    return res.data;
  },
  async updateDietaryRestriction(id: number, data: Partial<DietaryRestriction>): Promise<DietaryRestriction> {
    const res = await axiosInstance.patch(API_ENDPOINTS.symptoms.dietaryRestrictionDetail(id), data);
    return res.data;
  },
  async deleteDietaryRestriction(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.dietaryRestrictionDetail(id));
  },

  // Food Logs
  async getFoodLogs(): Promise<FoodLog[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.symptoms.foodLogs);
    const d = res.data; return Array.isArray(d) ? d : d.results || [];
  },
  async createFoodLog(data: Partial<FoodLog>): Promise<FoodLog> {
    const res = await axiosInstance.post(API_ENDPOINTS.symptoms.foodLogs, data);
    return res.data;
  },
  async deleteFoodLog(id: number): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.symptoms.foodLogDetail(id));
  },

  // AI Categorization
  async categorizeSymptoms(): Promise<{ categorized: number; total: number; categories: Record<string, string> }> {
    const res = await axiosInstance.post('/api/symptoms/symptoms/categorize/');
    return res.data;
  },
  async getSymptomsByCategory(): Promise<Record<string, Symptom[]>> {
    const res = await axiosInstance.get('/api/symptoms/symptoms/by-category/');
    return res.data;
  },
};

export default symptomsService;
